import type { NextPage } from "next";
import { useRouter } from "next/router";
import Head from "next/head";
import {
  FC,
  FunctionComponent,
  ReactNode,
  useState,
  useEffect,
  PropsWithChildren,
} from "react";
import { HiOutlineCode, HiOutlineDatabase, HiOutlineKey } from "react-icons/hi";

import { FactStats, PolicyStats, QueryStats, Stats, Token } from "../src/types";
import Button from "../components/Button";
import useEnv from "../src/useEnv";
import useData from "../src/useData";
import useTokens from "../src/useTokens";
import { AiOutlineFileSearch } from "react-icons/ai";
import WithIcon from "../components/WithIcon";
import Spinner from "../components/Spinner";
import { prettyAccessLevel } from "../src/prettyAccessLevel";
import { format } from "date-fns";
import LoadingPage from "@/components/LoadingPage";
import Modal from "@/components/Modal";
import useTenant from "@/src/useTenant";
import { CopyableCodeBlockPolar } from "@/components/CopyableCodeBlock";
import InlineCode from "@/components/InlineCode";
import { useSegmentCallbackV2 } from "../src/segment";
import MainContent from "@/components/MainContent";

const DashboardWidget: FC<
  PropsWithChildren<{
    heading: string;
    icon: ReactNode;
    href: string;
    highlight?: boolean;
    disable?: boolean;
  }>
> = ({ heading, icon, href, highlight, disable, children }) => {
  return (
    <div
      className={`rounded-lg p-4 h-64 relative bg-card ${
        highlight
          ? "border-8 p-2 border-oso-purple"
          : "border border-border p-4"
      }`}
    >
      {disable ? (
        <div className="absolute rounded-lg inset-0 z-10 bg-card/80 cursor-not-allowed" />
      ) : undefined}
      <div className="flex justify-between">
        <div className="font-space text-2xl">
          <WithIcon icon={icon}>{heading}</WithIcon>
        </div>
        <Button medium href={href}>
          Manage {heading}
        </Button>
      </div>
      {children}
    </div>
  );
};

const Loading: FunctionComponent = () => (
  <div className="flex flex-col items-center justify-center h-full -mt-6">
    <Spinner size={42} />
  </div>
);

const PolicyStatsWidget: FC<{
  policyStats: PolicyStats | undefined;
  stepsCompleted: number;
}> = ({ policyStats, stepsCompleted }) => {
  const currentStep = 1;
  return (
    <DashboardWidget
      icon={<HiOutlineCode />}
      heading="Rules"
      href="/rules-editor"
      disable={stepsCompleted + 1 < currentStep} // not possible but here for consistency
      highlight={false}
    >
      {policyStats ? (
        <div className="flex flex-col h-full -mt-6 justify-center items-center space-y-4">
          <div>
            <div>
              <span className="font-bold">Last Updated: </span>
              {policyStats.recent_versions.length > 0
                ? format(
                    new Date(policyStats.recent_versions[0].created_at),
                    "yyyy-MM-dd h:mm:ssaaa"
                  )
                : "never"}
            </div>
            <div>
              <span className="font-bold">Current Version: </span>
              {policyStats.total_versions === null
                ? "none"
                : policyStats.recent_versions[0].id}
            </div>
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </DashboardWidget>
  );
};

const FactStatsWidget: FC<{
  factStats: FactStats | undefined;
  stepsCompleted: number;
}> = ({ factStats, stepsCompleted }) => {
  const currentStep = 2;
  const factRows: [string, number][] = [];
  if (factStats) {
    for (const k in factStats.predicates) {
      if (factStats.predicates[k] > 0) {
        factRows.push([k, factStats.predicates[k]]);
      }
    }
    factRows.sort((a, b) => b[1] - a[1]); // Descending by count
  }
  return (
    <DashboardWidget
      icon={<HiOutlineDatabase />}
      heading="Data"
      href="/facts"
      disable={stepsCompleted + 1 < currentStep}
      highlight={false}
    >
      {factStats ? (
        <div className="overflow-auto h-5/6 mt-2 ml-4">
          <div className="sticky top-0 font-bold flex space-x-4 bg-card">
            <div className="w-3/5">Predicate</div>
            <div className="w-2/5">Number of Facts</div>
          </div>
          <div className="flex space-x-4">
            <div className="w-3/5 font-space">TOTAL</div>
            <div className="w-2/5">{factStats.total}</div>
          </div>
          {factRows.map(([predicate, count]) => (
            <div key={predicate} className="flex space-x-4">
              <code className="w-3/5 whitespace-nowrap overflow-x-auto no-scroll-bar">
                {predicate}
              </code>
              <div className="w-2/5">{count}</div>
            </div>
          ))}
        </div>
      ) : (
        <Loading />
      )}
    </DashboardWidget>
  );
};

const QueryStatsWidget: FC<{
  queryStats: QueryStats | undefined;
  stepsCompleted: number;
}> = ({ queryStats, stepsCompleted }) => {
  const currentStep = 3;

  return (
    <DashboardWidget
      icon={<AiOutlineFileSearch />}
      heading="Queries"
      href="/logs"
      disable={stepsCompleted + 1 < currentStep}
      highlight={false}
    >
      {queryStats ? (
        <div className="flex flex-col h-full -mt-6 justify-center space-y-4">
          <div className="text-center">
            {queryStats.count === 0 ? (
              <>Step {currentStep}: Learn about authorization queries</>
            ) : (
              <>{queryStats.count} queries in the past 24 hours.</>
            )}
          </div>
          <div className="flex justify-center">
            <Button medium theme="primary" href="/explain">
              Run Queries
            </Button>
          </div>
        </div>
      ) : (
        <Loading />
      )}
    </DashboardWidget>
  );
};

const ApiKeysWidget: FC<{
  apiKeys: Token[] | undefined;
  stepsCompleted: number;
}> = ({ apiKeys, stepsCompleted }) => {
  const currentStep = 4;

  return (
    <DashboardWidget
      icon={<HiOutlineKey />}
      heading="API Keys"
      href="/settings?api-keys"
      disable={stepsCompleted + 1 < currentStep}
      highlight={false}
    >
      {apiKeys ? (
        <div className="overflow-auto h-5/6">
          <div className="sticky top-0 grid grid-cols-2 space-x-4 font-bold bg-card">
            <div className="text-right">API Key ID</div>
            <div>Access Level</div>
          </div>
          {apiKeys.map(({ pub_id, access_level }) => (
            <div key={pub_id} className="grid grid-cols-2 space-x-4">
              <code className="text-right">{pub_id}</code>
              <div>{prettyAccessLevel(access_level)}</div>
            </div>
          ))}
        </div>
      ) : (
        <Loading />
      )}
    </DashboardWidget>
  );
};

const DashboardWithData: FunctionComponent = () => {
  const { selectedEnv } = useEnv();
  const { data: stats } = useData<Stats>(
    () => selectedEnv && `/web/environment/${selectedEnv.pub_id}/api/stats`
  );
  const { data: queryStats } = useData<QueryStats>(
    () => selectedEnv && `/web/environment/${selectedEnv.pub_id}/query_count`
  );
  const { data: apiKeys } = useTokens(selectedEnv?.pub_id);

  let stepsCompleted = 4;
  if (apiKeys && apiKeys.length === 0) stepsCompleted = 3;
  if (queryStats && queryStats.count === 0) stepsCompleted = 2;
  if (stats && stats.facts.total === 0) stepsCompleted = 1;
  if (stats && stats.policy.total_versions === null) stepsCompleted = 0;
  // if API keys are set, consider all steps completed
  if (apiKeys && apiKeys.length > 0) stepsCompleted = 4;

  return (
    <>
      <div className="font-space text-3xl">Dashboard</div>
      <div className="mt-5" />
      <div className="max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <PolicyStatsWidget
            policyStats={stats?.policy}
            stepsCompleted={stepsCompleted}
          />
          <FactStatsWidget
            factStats={stats?.facts}
            stepsCompleted={stepsCompleted}
          />
          <QueryStatsWidget
            queryStats={queryStats}
            stepsCompleted={stepsCompleted}
          />
          <ApiKeysWidget apiKeys={apiKeys} stepsCompleted={stepsCompleted} />
        </div>
      </div>
    </>
  );
};

const ModelerImport: React.FC<{
  isOpen: boolean;
  closeModal: () => void;
  modelerPolicy: string | null;
}> = ({ isOpen, closeModal, modelerPolicy }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [importComplete, setImportComplete] = useState<boolean>(false);

  const segmentCallbackV2 = useSegmentCallbackV2();
  const router = useRouter();
  const { switchTenant } = useTenant();
  const { selectedEnv } = useEnv();

  const MODELER_IMPORT_SEGMENT_SUCCESS: string = "Successful Modeler Import";
  const MODELER_IMPORT_SEGMENT_FAILURE: string = "Failed Modeler Import";
  const MODELER_IMPORT_SEGMENT_CANCEL: string = "Canceled Modeler Import";

  if (modelerPolicy === null || selectedEnv === null) {
    return null;
  }
  const productionEnv: boolean = selectedEnv.name === "production";

  if (importComplete) {
    return (
      <Modal
        wide
        isOpen={isOpen}
        onClose={() => {
          /* ignore onClose */
        }}
      >
        <h1 className="text-xl font-bold">Modeler Import</h1>
        <div className="flex flex-col gap-4 mt-4">
          <p>Import completed successfully! Redirecting to Rules Editor...</p>
        </div>
      </Modal>
    );
  }

  const onImportClick = async () => {
    // attempt to POST /policy
    setLoading(true);
    const resp = await fetch(
      `/web/environment/${selectedEnv.pub_id}/api/policy`,
      {
        method: "POST",
        body: JSON.stringify({
          src: modelerPolicy,
        }),
      }
    );
    if (resp.ok) {
      try {
        localStorage.removeItem("oso-modeler-import");
      } catch (e) {
        console.error("Failed to remove Modeler Import Policy", e);
      }
      // success
      setImportComplete(true);
      segmentCallbackV2(MODELER_IMPORT_SEGMENT_SUCCESS, {
        environment_pub_id: selectedEnv.pub_id,
        modelerPolicy: modelerPolicy,
      });
      router.push({ pathname: "/rules-editor", query: { ...router.query } });
    } else {
      // failure
      const error = await resp.json();
      setError(
        `Sorry, we encountered an error trying to import!\n\n${error.message}`
      );
      segmentCallbackV2(MODELER_IMPORT_SEGMENT_FAILURE, {
        environment_pub_id: selectedEnv.pub_id,
        error: error.message,
      });
    }
    // cleanup
    setLoading(false);
  };

  return (
    <Modal
      wide
      isOpen={isOpen}
      onClose={() => {
        /* ignore onClose */
      }}
    >
      <h1 className="text-xl font-bold">Modeler Import</h1>
      <div className="flex flex-col gap-4 mt-4">
        <p>
          We found a Modeler policy to import into your{" "}
          <InlineCode>{selectedEnv.name}</InlineCode> environment:
        </p>
        <div className="h-64 overflow-auto border border-border rounded-md">
          <CopyableCodeBlockPolar code={modelerPolicy} />
        </div>
        {productionEnv && (
          <p className="font-bold text-error">
            This is a production environment: you must import this policy
            manually.
          </p>
        )}
        <div className="flex flex-row justify-center gap-4 mt-2">
          <Button
            medium
            theme="primary"
            disabled={productionEnv}
            loading={loading}
            onClick={onImportClick}
          >
            Import
          </Button>
          <Button
            medium
            theme="secondary"
            onClick={() => {
              setError(null);
              switchTenant();
            }}
          >
            Switch Environment
          </Button>
          <Button
            medium
            theme="secondary"
            onClick={() => {
              segmentCallbackV2(MODELER_IMPORT_SEGMENT_CANCEL, {
                environment_pub_id: selectedEnv.pub_id,
                modelerPolicy: modelerPolicy,
              });
              try {
                localStorage.removeItem("oso-modeler-import");
                closeModal();
              } catch (e) {
                console.error("Failed to remove Modeler Import Policy", e);
              }
            }}
          >
            Cancel Import
          </Button>
        </div>
        {error && (
          <pre
            className={`text-xs overflow-auto leading-loose text-error bg-error-subtle rounded p-4`}
          >
            <div>{error}</div>
          </pre>
        )}
      </div>
    </Modal>
  );
};

const Dashboard: NextPage = () => {
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(true);
  // modeler import
  const [modelerPolicy, setModelerPolicy] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  useEffect(() => {
    if (router.isReady) {
      try {
        const policyToImport: string | null =
          localStorage.getItem("oso-modeler-import");
        setModelerPolicy(policyToImport);
        setImportModalOpen(policyToImport !== null);
      } catch (e) {
        console.error("Failed to read local storage for modeler import", e);
      }
      setLoading(false);
    }
  }, [router.isReady]);

  if (loading) return <LoadingPage />;

  return (
    <>
      <Head>
        <title>Oso Cloud - Dashboard</title>
      </Head>
      <ModelerImport
        isOpen={importModalOpen}
        closeModal={() => setImportModalOpen(false)}
        modelerPolicy={modelerPolicy}
      />
      <MainContent>
        <DashboardWithData />
      </MainContent>
    </>
  );
};

export default Dashboard;
